<template>
  <div>
    <v-row class="px-6 mb-4">
      <v-col cols="2">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('tslOrders.orderNumber')"
        />
      </v-col>
      <v-col cols="2" offset="4">
        <v-btn
          outlined
          @click="clearFilters()"
          block
          :disabled="!hasActiveFilters()"
        >
          {{ $t('tslOrders.filter.clearFilters') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn
          color="primary"
          @click="filterDialog = true"
          block
          depressed
        >
          {{ $t('tslOrders.filter.filter') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn
          outlined
          color="primary"
          @click="configDialog = true"
          block
        >
          <v-icon left>mdi-cog</v-icon>
          {{ $t('tslOrders.configureColumns') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="orders"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="page"
      :items-per-page="pageSize"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalCount"
      @update:sort-by="onSortChange"
      @update:sort-desc="onSortChange"
      class="clickable-rows"
      @click:row="showPreview"
    >
      <template v-slot:[`item.clientPriceWithCurrency`]="{ item }">
        {{ formatPrice(item.clientPrice, item.clientCurrency) }}
      </template>

      <template v-slot:[`item.carrierPriceWithCurrency`]="{ item }">
        {{ formatPrice(item.carrierPrice, item.carrierCurrency) }}
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template v-slot:[`item.loadingPlace`]="{ item }">
        <div v-if="item.allLoadingPlaces && item.allLoadingPlaces.length > 0" class="d-flex align-center">
          <div class="d-flex align-center">
            <CountryFlag :country="item.allLoadingPlaces[0].country" class="mr-1"/>
            <div class="d-flex flex-column">
              <span>
                {{ item.allLoadingPlaces[0].country.toUpperCase() }},
                {{ item.allLoadingPlaces[0].postalCode }}<br/>
                {{ item.allLoadingPlaces[0].city }}
              </span>
            </div>
          </div>
          <v-tooltip v-if="item.allLoadingPlaces.length > 1" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="more-data ml-2">
                +{{ item.allLoadingPlaces.length - 1 }}
              </div>
            </template>
            <div v-for="(location, index) in item.allLoadingPlaces" :key="index">
              <div v-if="index !== 0" class="d-flex align-center" style="line-height: 1.25">
                <CountryFlag :country="location.country" class="mr-1" />
                <div class="d-flex flex-column">
                  <span>{{ location.country.toUpperCase() }}, {{ location.postalCode }} {{ location.city }}</span>
                </div>
              </div>
            </div>
          </v-tooltip>
        </div>
        <div v-else>-</div>
      </template>

      <template v-slot:[`item.loadingDate`]="{ item }">
        <div v-if="item.allLoadingPlaces && item.allLoadingPlaces.length > 0" class="d-flex align-center">
          <div>{{ formatDateTime(item.allLoadingPlaces[0].dateTime, item.allLoadingPlaces[0].timeFrom, item.allLoadingPlaces[0].timeTo) }}</div>
          <v-tooltip v-if="item.allLoadingPlaces.length > 1" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="more-data ml-2">
                +{{ item.allLoadingPlaces.length - 1 }}
              </div>
            </template>
            <div v-for="(location, index) in item.allLoadingPlaces" :key="index">
              <div v-if="index !== 0">
                {{ formatDateTime(location.dateTime, location.timeFrom, location.timeTo) }}
              </div>
            </div>
          </v-tooltip>
        </div>
        <div v-else>-</div>
      </template>

      <template v-slot:[`item.unloadingPlace`]="{ item }">
        <div v-if="item.allUnloadingPlaces && item.allUnloadingPlaces.length > 0" class="d-flex align-center">
          <div class="d-flex align-center">
            <CountryFlag :country="item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].country" class="mr-1"/>
            <div class="d-flex flex-column">
              <span>
                {{ item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].country.toUpperCase() }},
                {{ item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].postalCode }}<br/>
                {{ item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].city }}
              </span>
            </div>
          </div>
          <v-tooltip v-if="item.allUnloadingPlaces.length > 1" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="more-data ml-2">
                +{{ item.allUnloadingPlaces.length - 1 }}
              </div>
            </template>
            <div v-for="(location, index) in item.allUnloadingPlaces" :key="index">
              <div v-if="index !== item.allUnloadingPlaces.length - 1" class="d-flex align-center" style="line-height: 1.25">
                <CountryFlag :country="location.country" class="mr-1" />
                <div class="d-flex flex-column">
                  <span>{{ location.country.toUpperCase() }}, {{ location.postalCode }} {{ location.city }}</span>
                </div>
              </div>
            </div>
          </v-tooltip>
        </div>
        <div v-else>-</div>
      </template>

      <template v-slot:[`item.unloadingDate`]="{ item }">
        <div v-if="item.allUnloadingPlaces && item.allUnloadingPlaces.length > 0" class="d-flex align-center">
          <div>{{ formatDateTime(item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].dateTime, item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].timeFrom, item.allUnloadingPlaces[item.allUnloadingPlaces.length - 1].timeTo) }}</div>
          <v-tooltip v-if="item.allUnloadingPlaces.length > 1" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="more-data ml-2">
                +{{ item.allUnloadingPlaces.length - 1 }}
              </div>
            </template>
            <div v-for="(location, index) in item.allUnloadingPlaces" :key="index">
              <div v-if="index !== item.allUnloadingPlaces.length - 1">
                {{ formatDateTime(location.dateTime, location.timeFrom, location.timeTo) }}
              </div>
            </div>
          </v-tooltip>
        </div>
        <div v-else>-</div>
      </template>

      <template v-slot:[`item.clientContractorName`]="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">{{ item.clientContractorName }}</span>
          <span v-if="item.clientVatNumber" class="text-caption grey--text">NIP: {{ item.clientVatNumber }}</span>
          <span v-if="item.clientAddress" class="text-caption grey--text">{{ item.clientAddress }}</span>
        </div>
      </template>

      <template v-slot:[`item.carrierContractorName`]="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">{{ item.carrierContractorName }}</span>
          <span v-if="item.carrierVatNumber" class="text-caption grey--text">NIP: {{ item.carrierVatNumber }}</span>
          <span v-if="item.carrierAddress" class="text-caption grey--text">{{ item.carrierAddress }}</span>
        </div>
      </template>

      <template v-slot:[`item.commission`]="{ item }">
        {{ item.commission }} EUR
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click.stop>
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-if="canSearchReload(item)"
              link
              @click.stop="searchReloadFromOrder(item)"
            >
              <v-list-item-title>{{ $t('tslOrders.searchReloadFromOrder') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canEdit(item)"
              link
              @click="editOrder(item)"
            >
              <v-list-item-title>{{ $t("tslOrders.edit") }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canChangeAssignment(item)"
              link
              @click="changeAssignment(item)"
            >
              <v-list-item-title>{{ $t("tslOrders.changeAssignment") }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canArchive(item)"
              link
              @click="archiveOrder(item)"
            >
              <v-list-item-title>{{ $t("tslOrders.archive") }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canRestore(item)"
              link
              @click="restoreOrder(item)"
            >
              <v-list-item-title>{{ $t("tslOrders.restore") }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="showHistory(item)">
              <v-list-item-title>{{ $t("tslOrders.showHistory") }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click.stop="duplicateOrder(item)">
              <v-list-item-title>{{ $t('tslOrders.duplicateOrder') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="page" :length="pageCount"></v-pagination>
      </template>
    </v-data-table>

    <OrderHistoryDialog
      v-model="historyDialog"
      :history-data="historyData"
      :loading="historyLoading"
      @close="historyDialog = false"
    />

    <OrderPreviewDialog
      v-model="previewDialog"
      :order-id="selectedOrderId"
      @close="previewDialog = false"
    />

    <ChangeAssignmentDialog
      v-model="assignmentDialog"
      :order-id="selectedOrderId"
      :current-assigned-user-id="selectedOrderAssignedUserId"
      @saved="onAssignmentChanged"
      @close="assignmentDialog = false"
    />

    <ColumnConfigDialog
      v-model="configDialog"
      :current-config="columnConfig"
      @save="saveColumnConfig"
    />

    <TSLOrdersFilterDialog
      v-model="filterDialog"
      :filters="filters"
      @close="filterDialog = false"
      @filter="applyFilters"
    />
  </div>
</template>

<script>
import Service from '@/services'
import OrderHistoryDialog from '../components/OrderHistoryDialog'
import OrderPreviewDialog from '../components/OrderPreviewDialog'
import ChangeAssignmentDialog from '../components/ChangeAssignmentDialog'
import ColumnConfigDialog from '../components/ColumnConfigDialog'
import TSLOrdersFilterDialog from '../components/TSLOrdersFilterDialog'
import CountryFlag from 'vue-country-flag'
import { getEnumDescription } from '@/utils/helpers'

export default {
  name: 'TSLOrdersTable',
  components: {
    OrderHistoryDialog,
    OrderPreviewDialog,
    ChangeAssignmentDialog,
    ColumnConfigDialog,
    TSLOrdersFilterDialog,
    CountryFlag
  },
  props: {
    showArchived: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      orders: [],
      searchInputValue: '',
      page: 1,
      pageSize: 20,
      totalCount: 0,
      searchTimeout: null,
      historyDialog: false,
      previewDialog: false,
      assignmentDialog: false,
      configDialog: false,
      filterDialog: false,
      selectedOrderId: null,
      selectedOrderAssignedUserId: null,
      historyLoading: false,
      historyData: [],
      columnConfig: null,
      sortBy: [],
      sortDesc: [],
      filters: {
        dateFrom: null,
        dateTo: null,
        carrierVatNumber: '',
        carrierName: '',
        clientVatNumber: '',
        clientName: '',
        driverName: '',
        driverPhone: '',
        loadingDateFrom: null,
        loadingDateTo: null,
        unloadingDateFrom: null,
        unloadingDateTo: null,
        loadingPlace: '',
        unloadingPlace: '',
        createdByUserId: null
      }
    }
  },
  computed: {
    pageCount () {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    currentUserId () {
      return this.$store.getters.userCommonData.id
    },
    isOwnerOrManager () {
      return this.$store.getters.isManagerOrOwner
    },
    headers () {
      const config = this.columnConfig || this.getDefaultColumnConfig()
      const headers = config
        .filter(col => col.visible)
        .map(col => ({
          text: col.label,
          value: col.key,
          // Wyłącz sortowanie dla kolumn obliczanych w frontendzie
          sortable: !['actions', 'loadingPlace', 'loadingDate', 'unloadingPlace', 'unloadingDate', 'clientPriceWithCurrency', 'carrierPriceWithCurrency'].includes(col.key)
        }))

      // Dodaj kolumnę akcji na końcu
      headers.push({
        text: this.$t('tslOrders.actions'),
        value: 'actions',
        sortable: false
      })

      return headers
    }
  },
  watch: {
    page () {
      this.loadOrders()
    },
    searchInputValue () {
      this.page = 1
      clearTimeout(this.searchTimeout)
      this.loading = true
      this.searchTimeout = setTimeout(() => {
        this.loadOrders()
      }, 1000)
    },
    showArchived () {
      this.page = 1
      this.loadOrders()
    }
  },
  created () {
    this.loadColumnConfig()
    this.loadOrders()
  },
  methods: {
    getDefaultColumnConfig () {
      return [
        // Kolumny obowiązkowe
        { key: 'createdAt', label: this.$t('tslOrders.createdAt'), visible: true, mandatory: true },
        { key: 'orderNumber', label: this.$t('tslOrders.orderNumber'), visible: true, mandatory: true },

        // Kolumny domyślne (widoczne)
        { key: 'loadingPlace', label: this.$t('tslOrders.loadingPlace'), visible: true, mandatory: false },
        { key: 'loadingDate', label: this.$t('tslOrders.loadingDate'), visible: true, mandatory: false },
        { key: 'unloadingPlace', label: this.$t('tslOrders.unloadingPlace'), visible: true, mandatory: false },
        { key: 'unloadingDate', label: this.$t('tslOrders.unloadingDate'), visible: true, mandatory: false },
        { key: 'clientContractorName', label: this.$t('tslOrders.clientName'), visible: true, mandatory: false },
        { key: 'carrierContractorName', label: this.$t('tslOrders.carrierName'), visible: true, mandatory: false },
        { key: 'clientPriceWithCurrency', label: this.$t('tslOrders.clientPriceWithCurrency'), visible: true, mandatory: false },
        { key: 'carrierPriceWithCurrency', label: this.$t('tslOrders.carrierPriceWithCurrency'), visible: true, mandatory: false },
        { key: 'commission', label: this.$t('tslOrders.commission'), visible: true, mandatory: false },

        // Kolumny dodatkowe (niewidoczne domyślnie)
        { key: 'clientPhone', label: this.$t('tslOrders.clientPhone'), visible: false, mandatory: false },
        { key: 'clientEmail', label: this.$t('tslOrders.clientEmail'), visible: false, mandatory: false },
        { key: 'clientRefNumber', label: this.$t('tslOrders.fields.clientRefNumber'), visible: false, mandatory: false },
        { key: 'driverFullName', label: this.$t('tslOrders.driverFullName'), visible: false, mandatory: false },
        { key: 'carrierPhone', label: this.$t('tslOrders.fields.carrierPhone'), visible: false, mandatory: false },
        { key: 'vehicleRegistrationNumber', label: this.$t('tslOrders.vehicleRegistrationNumber'), visible: false, mandatory: false },
        { key: 'carrierVehicleType', label: this.$t('tslOrders.vehicleType'), visible: false, mandatory: false },
        { key: 'createdByUserName', label: this.$t('tslOrders.assignedEmployee'), visible: false, mandatory: false }
      ]
    },
    loadColumnConfig () {
      const userId = this.currentUserId
      const storageKey = `tslOrdersTableColumns_${userId}`
      try {
        const savedConfig = localStorage.getItem(storageKey)
        if (savedConfig) {
          this.columnConfig = JSON.parse(savedConfig)
        } else {
          this.columnConfig = this.getDefaultColumnConfig()
        }
      } catch (e) {
        console.error('Error loading column config:', e)
        this.columnConfig = this.getDefaultColumnConfig()
      }
    },
    saveColumnConfig (config) {
      const userId = this.currentUserId
      const storageKey = `tslOrdersTableColumns_${userId}`
      try {
        localStorage.setItem(storageKey, JSON.stringify(config))
        this.columnConfig = config
        this.$store.dispatch('setSuccessNotification', this.$t('common.confirmOperation'))
      } catch (e) {
        console.error('Error saving column config:', e)
        this.$store.dispatch('setErrorNotification', 'Error saving configuration')
      }
    },
    onSortChange () {
      // Resetuj do pierwszej strony przy zmianie sortowania
      this.page = 1
      this.loadOrders()
    },
    formatPrice (price, currencyId) {
      if (!price) return '-'
      const currencyName = getEnumDescription(this.$store.getters.allCurrencies, currencyId)
      return `${price} ${currencyName}`
    },
    formatLocation (location) {
      if (!location) return '-'
      if (typeof location === 'string') return location
      // Zakładamy że location może być obiektem z polami city, country
      return location.city || location.country || '-'
    },
    formatDate (date) {
      if (!date) return '-'
      return this.$options.filters.dateFormat(date, 'DD.MM.YYYY')
    },
    formatDateTime (dateTime, timeFrom, timeTo) {
      if (!dateTime) return ''
      let result = this.$options.filters.dateFormat(dateTime, 'DD.MM.YYYY')
      if (timeFrom && timeTo) {
        const formatTime = (timeSpan) => {
          // timeSpan jest obiektem TimeSpan z backendu (przekonwertowanym na string lub obiekt)
          if (typeof timeSpan === 'string') {
            // Jeśli to string w formacie "HH:mm:ss"
            const parts = timeSpan.split(':')
            return `${parts[0]}:${parts[1]}`
          } else if (typeof timeSpan === 'number') {
            // Jeśli to liczba sekund
            const hours = Math.floor(timeSpan / 3600)
            const minutes = Math.floor((timeSpan % 3600) / 60)
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
          }
          return ''
        }
        const fromStr = formatTime(timeFrom)
        const toStr = formatTime(timeTo)
        if (fromStr && toStr) {
          result += ` ${fromStr}-${toStr}`
        }
      }
      return result
    },
    hasActiveFilters () {
      return this.filters.dateFrom ||
             this.filters.dateTo ||
             this.filters.carrierVatNumber ||
             this.filters.carrierName ||
             this.filters.clientVatNumber ||
             this.filters.clientName ||
             this.filters.driverName ||
             this.filters.driverPhone ||
             this.filters.loadingDateFrom ||
             this.filters.loadingDateTo ||
             this.filters.unloadingDateFrom ||
             this.filters.unloadingDateTo ||
             this.filters.loadingPlace ||
             this.filters.unloadingPlace ||
             this.filters.createdByUserId
    },
    clearFilters () {
      this.filters = {
        dateFrom: null,
        dateTo: null,
        carrierVatNumber: '',
        carrierName: '',
        clientVatNumber: '',
        clientName: '',
        driverName: '',
        driverPhone: '',
        loadingDateFrom: null,
        loadingDateTo: null,
        unloadingDateFrom: null,
        unloadingDateTo: null,
        loadingPlace: '',
        unloadingPlace: '',
        createdByUserId: null
      }
      this.page = 1
      this.loadOrders()
    },
    applyFilters (filters) {
      this.filters = filters
      this.page = 1
      this.filterDialog = false
      this.loadOrders()
    },
    async loadOrders () {
      this.loading = true
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          search: this.searchInputValue || null,
          sortBy: this.sortBy[0] || null,
          sortDesc: this.sortDesc[0] || false,
          showArchived: this.showArchived,
          dateFrom: this.filters.dateFrom || null,
          dateTo: this.filters.dateTo || null,
          carrierVatNumber: this.filters.carrierVatNumber || null,
          carrierName: this.filters.carrierName || null,
          clientVatNumber: this.filters.clientVatNumber || null,
          clientName: this.filters.clientName || null,
          driverName: this.filters.driverName || null,
          driverPhone: this.filters.driverPhone || null,
          loadingDateFrom: this.filters.loadingDateFrom || null,
          loadingDateTo: this.filters.loadingDateTo || null,
          unloadingDateFrom: this.filters.unloadingDateFrom || null,
          unloadingDateTo: this.filters.unloadingDateTo || null,
          loadingPlace: this.filters.loadingPlace || null,
          unloadingPlace: this.filters.unloadingPlace || null,
          createdByUserId: this.filters.createdByUserId || null
        }
        const { data } = await Service.get('/TSLOrder/GetOrders', params)
        this.orders = data.results
        this.totalCount = data.count
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error loading orders')
      } finally {
        this.loading = false
      }
    },
    editOrder (item) {
      this.$router.push({
        name: 'AddTSLOrder',
        params: { id: item.id }
      })
    },
    duplicateOrder (item) {
      this.$router.push({
        name: 'CopyTSLOrder',
        params: { id: item.id }
      })
    },
    async searchReloadFromOrder (item) {
      if (!item || !item.id) return

      this.loading = true
      try {
        const { data: filterId } = await Service.post('Filter/CreateForTSLOrder', {
          tslOrderId: item.id
        })

        this.$router.push({
          name: 'ReloadsFilter',
          params: {
            id: filterId
          }
        })
      } catch (e) {
        await this.$store.dispatch('setErrorNotification',
          e.response?.data || this.$t('tslOrders.messages.searchReloadError'))
      } finally {
        this.loading = false
      }
    },
    showHistory (item) {
      this.selectedOrderId = item.id
      this.historyDialog = true
      this.loadHistory()
    },
    async loadHistory () {
      this.historyLoading = true
      try {
        const { data } = await Service.get('/TSLOrder/GetOrderHistory', {
          orderId: this.selectedOrderId
        })
        this.historyData = data
      } catch (e) {
        this.$store.dispatch('setErrorNotification',
          e.response?.data || 'Error loading history')
      } finally {
        this.historyLoading = false
      }
    },
    canEdit (order) {
      if (order.archived) return false
      if (this.isOwnerOrManager) return true
      return order.createdByUserId === this.currentUserId
    },
    canSearchReload (order) {
      if (order.archived) return false
      return order.allUnloadingPlaces && order.allUnloadingPlaces.length > 0
    },
    canChangeAssignment (order) {
      if (order.archived) return false
      return this.isOwnerOrManager
    },
    showPreview (order) {
      this.selectedOrderId = order.id
      this.previewDialog = true
    },
    changeAssignment (order) {
      this.selectedOrderId = order.id
      this.selectedOrderAssignedUserId = order.createdByUserId
      this.assignmentDialog = true
    },
    onAssignmentChanged () {
      this.assignmentDialog = false
      this.loadOrders()
    },
    canArchive (order) {
      return !order.archived
    },
    canRestore (order) {
      return order.archived
    },
    async archiveOrder (order) {
      try {
        await Service.post('/TSLOrder/ArchiveOrder', { orderId: order.id })
        this.$store.dispatch('setSuccessNotification', this.$t('tslOrders.messages.orderArchived'))
        this.loadOrders()
      } catch (e) {
        this.$store.dispatch('setErrorNotification',
          e.response?.data || 'Error archiving order')
      }
    },
    async restoreOrder (order) {
      try {
        await Service.post('/TSLOrder/RestoreOrder', { orderId: order.id })
        this.$store.dispatch('setSuccessNotification', this.$t('tslOrders.messages.orderRestored'))
        this.loadOrders()
      } catch (e) {
        this.$store.dispatch('setErrorNotification',
          e.response?.data || 'Error restoring order')
      }
    }
  }
}
</script>

<style scoped>
.clickable-rows >>> tbody tr {
  cursor: pointer;
}
.clickable-rows >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
